// Small WebAudio toolkit for the map soundscapes and scores. Every node is created through a Kit, which counts it (QA: node
// budget per map) and owns it: dispose() stops every source and, once they have ended, disconnects the whole graph, so a run
// ending never leaves anything running. The map audio builds all of its nodes once at the start of a run (persistent voices
// with re-triggered envelopes), so nothing is allocated per note or per frame.
import type { EchoSpec, NoiseColor } from './mapAudioDef';
import { makeNoise, mulberry32 } from './noise';

const NOISE_SECONDS: Record<NoiseColor, number> = { white: 2, pink: 3, brown: 4 };
const banks = new WeakMap<BaseAudioContext, Partial<Record<NoiseColor, AudioBuffer>>>();

/** Shared stereo noise loops, built once per audio context (reused by every map and every run). */
export function noiseBuffer(ac: BaseAudioContext, color: NoiseColor): AudioBuffer {
  let bank = banks.get(ac); if (!bank) { bank = {}; banks.set(ac, bank); }
  let b = bank[color];
  if (!b) {
    const n = Math.floor(ac.sampleRate * NOISE_SECONDS[color]), rand = mulberry32(color === 'white' ? 11 : color === 'pink' ? 23 : 37);
    b = ac.createBuffer(2, n, ac.sampleRate);
    for (let ch = 0; ch < 2; ch++) b.getChannelData(ch).set(makeNoise(color, n, rand));
    bank[color] = b;
  }
  return b;
}

/** Time-constant helper: setTargetAtTime reaches ~95% in 3 tc. */
export const tc = (sec: number): number => Math.max(0.0005, sec / 3);

export class Kit {
  readonly nodes: AudioNode[] = [];
  readonly sources: AudioScheduledSourceNode[] = [];
  private disposed = false;
  constructor(readonly ac: BaseAudioContext, readonly rand: () => number) {}

  private own<T extends AudioNode>(n: T): T { this.nodes.push(n); return n; }
  private src<T extends AudioScheduledSourceNode>(n: T): T { this.own(n); this.sources.push(n); return n; }

  gain(v = 0): GainNode { const g = this.own(this.ac.createGain()); g.gain.value = v; return g; }
  filter(type: BiquadFilterType, f: number, q = 0.707): BiquadFilterNode {
    const b = this.own(this.ac.createBiquadFilter()); b.type = type; b.frequency.value = f; b.Q.value = q; return b;
  }
  pan(p = 0): StereoPannerNode { const s = this.own(this.ac.createStereoPanner()); s.pan.value = p; return s; }
  /** a running oscillator (started now, stopped by dispose) */
  osc(type: OscillatorType, f: number, detune = 0): OscillatorNode {
    const o = this.src(this.ac.createOscillator()); o.type = type; o.frequency.value = f; o.detune.value = detune; o.start(); return o;
  }
  /** a running looped noise source at a random offset (layers sharing a buffer never phase) */
  noise(color: NoiseColor, rate = 1): AudioBufferSourceNode {
    const s = this.src(this.ac.createBufferSource()); s.buffer = noiseBuffer(this.ac, color); s.loop = true; s.playbackRate.value = rate;
    s.start(this.ac.currentTime, this.rand() * s.buffer.duration); return s;
  }
  shaper(amount: number): WaveShaperNode {
    const ws = this.own(this.ac.createWaveShaper()), n = 512, curve = new Float32Array(n);
    for (let i = 0; i < n; i++) { const x = (i / (n - 1)) * 2 - 1; curve[i] = Math.tanh(x * amount) / Math.tanh(amount); }
    ws.curve = curve; return ws;
  }
  /** stereo ping-pong feedback delay; returns its input. Output goes to `out`. */
  echo(spec: EchoSpec, out: AudioNode): GainNode {
    const inp = this.gain(1), wet = this.gain(spec.wet);
    const dl = this.own(this.ac.createDelay(2)), dr = this.own(this.ac.createDelay(2));
    dl.delayTime.value = Math.min(1.9, spec.l); dr.delayTime.value = Math.min(1.9, spec.r);
    const lpl = this.filter('lowpass', spec.damp), lpr = this.filter('lowpass', spec.damp), fbl = this.gain(spec.fb), fbr = this.gain(spec.fb);
    const pl = this.pan(-0.6), pr = this.pan(0.6);
    inp.connect(dl); dl.connect(lpl).connect(fbl).connect(dr); dr.connect(lpr).connect(fbr).connect(dl);
    dl.connect(pl).connect(wet); dr.connect(pr).connect(wet); wet.connect(out);
    return inp;
  }

  /** stop every source at `at` and disconnect the graph once they have ended */
  dispose(at: number): void {
    if (this.disposed) return; this.disposed = true;
    const all = this.nodes.slice();
    const unhook = () => { for (const n of all) { try { n.disconnect(); } catch { /* already */ } } this.nodes.length = 0; this.sources.length = 0; };
    if (!this.sources.length) { unhook(); return; }
    this.sources[0].onended = unhook;
    for (const s of this.sources) { try { s.stop(at); } catch { /* already stopped */ } }
  }
  get isDisposed(): boolean { return this.disposed; }
}
